import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ShieldOff, RefreshCw, Search } from 'lucide-react';
import { AgencyTierBadge, type AgencyTier } from '@/components/agencies/AgencyTierBadge';
import { tierLabel } from '@/lib/tierMapping';

interface AgencyRow {
  id: string;
  name: string;
  city: string | null;
  is_verified: boolean | null;
  created_by: string | null;
}

interface FeaturedRow {
  id: string;
  listing_id: string;
  listing_type: string;
  tier: string;
  placement: string | null;
  priority: number | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  subscription_id: string | null;
  source: string | null;
  notes: string | null;
  created_at: string;
}

export default function AgencyTierDebug() {
  const { hasRole, loading } = useAuth();
  const allowed = hasRole('superadmin') || hasRole('ad_operations_lead');

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: agencies, isLoading: agLoading } = useQuery({
    queryKey: ['debug-agencies', search],
    queryFn: async () => {
      let q = supabase
        .from('agencies')
        .select('id,name,city,is_verified,created_by')
        .order('created_at', { ascending: false })
        .limit(50);
      if (search.trim()) q = q.ilike('name', `%${search.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as AgencyRow[];
    },
    enabled: allowed,
  });

  const { data: tierRow, isLoading: tierLoading, refetch: refetchTier } = useQuery({
    queryKey: ['debug-agency-tier', selectedId],
    enabled: allowed && !!selectedId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agency_featured_tier' as any)
        .select('*')
        .eq('agency_id', selectedId!)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return (data as unknown) as { agency_id: string; tier: AgencyTier } | null;
    },
  });

  const { data: featuredRows, isLoading: flLoading, refetch: refetchFeatured } = useQuery({
    queryKey: ['debug-featured-listings', selectedId],
    enabled: allowed && !!selectedId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('featured_listings')
        .select('*')
        .eq('listing_type', 'agency')
        .eq('listing_id', selectedId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as FeaturedRow[];
    },
  });

  const selected = useMemo(
    () => agencies?.find((a) => a.id === selectedId) || null,
    [agencies, selectedId],
  );

  const today = new Date().toISOString().slice(0, 10);
  const expectedActive = (featuredRows || []).filter(
    (r) => r.is_active && r.start_date <= today && r.end_date >= today,
  );
  const tierRank = (t: string) => (t === 'super_hot' ? 3 : t === 'hot' ? 2 : t === 'standard' ? 1 : 0);
  const expectedTier = expectedActive
    .slice()
    .sort((a, b) => tierRank(b.tier) - tierRank(a.tier))[0]?.tier as AgencyTier | undefined;

  const viewTier = (tierRow?.tier ?? null) as AgencyTier;
  const matches = (expectedTier || null) === (viewTier || null);

  if (loading) {
    return (
      <Layout>
        <div className="container py-8"><Skeleton className="h-32 w-full" /></div>
      </Layout>
    );
  }

  if (!allowed) {
    return (
      <Layout>
        <div className="container py-12 max-w-md text-center">
          <ShieldOff className="h-10 w-10 mx-auto text-destructive mb-3" />
          <h1 className="text-xl font-bold mb-1">Access denied</h1>
          <p className="text-sm text-muted-foreground">
            This troubleshooting page is restricted to SuperAdmin and Ad Operations Lead roles.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title="Agency Tier Debug" description="Inspect agency_featured_tier and featured_listings rows." noindex />
      <div className="container py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Agency Featured Tier — Debug</h1>
          <p className="text-sm text-muted-foreground">
            Inspect the live <code>agency_featured_tier</code> view and the underlying
            <code> featured_listings</code> rows for any agency.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">1. Pick an agency</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label htmlFor="search">Search by name</Label>
                <Input
                  id="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. Noah Real Estate"
                />
              </div>
              <Button variant="outline" size="icon" onClick={() => refetchTier()}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="rounded-md border max-h-72 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agLoading ? (
                    <TableRow><TableCell colSpan={4}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                  ) : (agencies || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">No agencies match.</TableCell></TableRow>
                  ) : agencies!.map((a) => (
                    <TableRow key={a.id} className={selectedId === a.id ? 'bg-muted/50' : ''}>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.city || '—'}</TableCell>
                      <TableCell>{a.is_verified ? <Badge variant="secondary">Verified</Badge> : <span className="text-xs text-muted-foreground">No</span>}</TableCell>
                      <TableCell>
                        <Button size="sm" variant={selectedId === a.id ? 'default' : 'outline'} onClick={() => setSelectedId(a.id)}>
                          Inspect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {selectedId && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">2. Tier resolution</CardTitle>
                <Button size="sm" variant="outline" onClick={() => { refetchTier(); refetchFeatured(); }}>
                  <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                </Button>
              </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-4">
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Selected agency</div>
                  <div className="font-semibold">{selected?.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 break-all">{selectedId}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase text-muted-foreground mb-1">View output</div>
                  {tierLoading ? <Skeleton className="h-6 w-24" /> : (
                    <>
                      <div className="text-sm">tier: <code>{String(viewTier ?? 'null')}</code></div>
                      <div className="mt-2">
                        {viewTier ? <AgencyTierBadge tier={viewTier} /> : <span className="text-xs text-muted-foreground">no badge</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        UI label: <strong>{tierLabel(viewTier ?? null, 'agency') ?? '—'}</strong>
                      </div>
                    </>
                  )}
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Expected (from featured_listings)</div>
                  {flLoading ? <Skeleton className="h-6 w-24" /> : (
                    <>
                      <div className="text-sm">tier: <code>{String(expectedTier ?? 'null')}</code></div>
                      <div className="mt-2">
                        {matches ? (
                          <Badge className="bg-emerald-600 text-white">Matches view ✓</Badge>
                        ) : (
                          <Badge variant="destructive">Mismatch — investigate</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Active rows today ({today}): {expectedActive.length}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">3. Underlying <code>featured_listings</code> rows</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tier</TableHead>
                        <TableHead>Active</TableHead>
                        <TableHead>Window</TableHead>
                        <TableHead>Placement</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flLoading ? (
                        <TableRow><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                      ) : (featuredRows || []).length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground">No featured_listings rows for this agency.</TableCell></TableRow>
                      ) : featuredRows!.map((r) => {
                        const inWindow = r.start_date <= today && r.end_date >= today;
                        const counted = r.is_active && inWindow;
                        return (
                          <TableRow key={r.id} className={counted ? '' : 'opacity-60'}>
                            <TableCell><code>{r.tier}</code></TableCell>
                            <TableCell>{r.is_active ? <Badge variant="secondary">active</Badge> : <Badge variant="outline">inactive</Badge>}</TableCell>
                            <TableCell className="text-xs">
                              {r.start_date} → {r.end_date}
                              {!inWindow && <div className="text-destructive">out of window</div>}
                            </TableCell>
                            <TableCell className="text-xs">{r.placement || '—'}</TableCell>
                            <TableCell className="text-xs">{r.priority ?? '—'}</TableCell>
                            <TableCell className="text-xs break-all">{r.subscription_id || '—'}</TableCell>
                            <TableCell className="text-xs">{r.source || '—'}</TableCell>
                            <TableCell className="text-xs max-w-[260px] truncate" title={r.notes || ''}>{r.notes || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  A row contributes to the view tier only when <strong>is_active = true</strong> and today falls inside the start/end window.
                  The view returns the highest-ranked active tier (super_hot &gt; hot &gt; standard).
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
